import { Form, DatePicker, Input, Select, message, Upload, Checkbox } from "antd";
import type { UploadFile } from "antd";
import { FC, useState, useMemo, useEffect } from "react";
import { Package, MapPin, Calendar, FileText, X, Plus, Upload as UploadIcon } from "lucide-react";
import { useAuth } from "~/store";
import dayjs from "dayjs";
import { TInventory } from "~/types";
import { useGetMyInventories } from "~/services/inventory";
import { CheckType, InventoryStatusCode, ServiceType, DocumentType } from "~/utils/constants";
import { InventorySelectionSidebar } from "./inventory-selection-sidebar";
import { fileServices } from "~/services/file";
import { useGetSubClients, useGetClient } from "~/services/client/client.queries";
import { useUpdateClient, useCreateServiceRequest } from "~/services/client/client.mutations";
import { useQueryClient } from "@tanstack/react-query";
import { Role } from "~/utils/constants";
import { userService } from "~/services/user/user.service";
import { notificationsAPI } from "~/services/notifications/notifications.api";

const getStatusBadge = (status: InventoryStatusCode) => {
  const statusConfig = {
    [InventoryStatusCode.AVAILABLE_ON_HAND]: {
      text: "Available On Hand",
      className: "bg-black text-white",
    },
    [InventoryStatusCode.AVAILABLE_LIVE]: {
      text: "Available Live",
      className: "bg-black text-white",
    },
    [InventoryStatusCode.NO_POSSESSION]: {
      text: "No Possession",
      className: "bg-gray-200 text-black",
    },
    [InventoryStatusCode.RELEASED]: {
      text: "Released",
      className: "bg-gray-200 text-black",
    },
  };
  const config =
    statusConfig[status] ||
    statusConfig[InventoryStatusCode.NO_POSSESSION];
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs font-bold uppercase border-2 border-black ${config.className}`}
    >
      {config.text}
    </span>
  );
};

const { TextArea } = Input;
const { Option } = Select;

interface RequestServiceSidebarProps {
  open: boolean;
  onClose: () => void;
  preSelectedInventoryIds?: string[];
}

type MovementType = "delivery_from_storage" | "collect_for_storage" | "transport" | "on_site_service";

export const RequestServiceSidebar: FC<RequestServiceSidebarProps> = ({
  open,
  onClose,
  preSelectedInventoryIds = [],
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isInventorySidebarOpen, setIsInventorySidebarOpen] = useState(false);
  const [selectedInventoryIds, setSelectedInventoryIds] = useState<string[]>([]);
  const [movementType, setMovementType] = useState<MovementType | undefined>(undefined);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const { user } = useAuth();
  const client = user?.client;
  const createServiceRequestMutation = useCreateServiceRequest();
  const updateClientMutation = useUpdateClient();
  const queryClient = useQueryClient();
  const [selectedSubClientId, setSelectedSubClientId] = useState<string | undefined>(undefined);
  const [preferredTimeWindowCheckbox, setPreferredTimeWindowCheckbox] = useState<string[]>([]);

  // Fetch subclients
  const { data: subClientsData } = useGetSubClients(client?.id || "");

  // Fetch selected subclient data to get their shipping addresses
  const { data: selectedSubClientData } = useGetClient(selectedSubClientId);

  // Determine which client's addresses to use
  const addressesToUse = useMemo(() => {
    if (selectedSubClientId && selectedSubClientData?.shippingAddresses) {
      return selectedSubClientData.shippingAddresses;
    }
    return client?.shippingAddresses || [];
  }, [selectedSubClientId, selectedSubClientData, client?.shippingAddresses]);

  // Fetch available inventory items - filter by subclient if selected
  const { data: inventoryData } = useGetMyInventories({
    checkParentId: CheckType.UNCHECK,
    status: InventoryStatusCode.AVAILABLE_ON_HAND,
    page: 1,
    pageSize: 100,
    subClientId: selectedSubClientId,
  });

  const selectedInventories = useMemo(() => {
    if (!inventoryData?.items) return [];
    return inventoryData.items.filter((item) =>
      selectedInventoryIds.includes(item.id)
    );
  }, [inventoryData?.items, selectedInventoryIds]);

  // Map movement type to ServiceType
  const mapMovementTypeToServiceType = (movementType: MovementType): ServiceType => {
    switch (movementType) {
      case "delivery_from_storage":
        return ServiceType.DELIVERY;
      case "collect_for_storage":
        return ServiceType.COLLECTION;
      case "transport":
        return ServiceType.COLLECTION; // Transport involves both pickup and delivery, but we'll use COLLECTION as primary
      case "on_site_service":
        return ServiceType.ON_SITE_SERVICE;
      default:
        return ServiceType.COLLECTION;
    }
  };

  // Format request details into notes
  const formatRequestNotes = (values: any, selectedInventory: TInventory[]): string => {
    const notes: string[] = [];
    
    notes.push("=== CLIENT SERVICE REQUEST ===");
    notes.push("");
    
    // Contact Information
    notes.push("CONTACT INFORMATION:");
    notes.push(`Name: ${values.contactName || `${client?.firstName} ${client?.lastName}`}`);
    notes.push(`Phone: ${values.contactPhone || client?.contactPhone || "N/A"}`);
    notes.push("");
    
    // Movement Type
    notes.push("MOVEMENT TYPE:");
    const movementTypeLabels: Record<MovementType, string> = {
      delivery_from_storage: "Delivery from Storage",
      collect_for_storage: "Collect for Storage/Hold",
      transport: "Transport",
      on_site_service: "On Site Service",
    };
    const movementType = values.movementType as MovementType;
    notes.push(movementTypeLabels[movementType] || values.movementType || "Unknown");
    notes.push("");
    
    // Addresses
    if (values.pickupAddress) {
      notes.push("PICKUP ADDRESS:");
      notes.push(values.pickupAddress);
      notes.push("");
    }
    if (values.deliveryAddress) {
      notes.push("DELIVERY ADDRESS:");
      notes.push(values.deliveryAddress);
      notes.push("");
    }
    if (values.serviceAddress) {
      notes.push("SERVICE ADDRESS:");
      notes.push(values.serviceAddress);
      notes.push("");
    }
    
    // Preferred Date & Time
    notes.push("PREFERRED DATE & TIME:");
    // Add preferred time window checkboxes
    if (preferredTimeWindowCheckbox.length > 0) {
      const timeWindowLabels: Record<string, string> = {
        ASAP: "ASAP",
        within_1_week: "Within 1 Week",
        "2_weeks": "2 Weeks",
        beyond_2_weeks: "Beyond 2 Weeks",
      };
      const labels = preferredTimeWindowCheckbox.map((val) => timeWindowLabels[val] || val);
      notes.push(`Preferred Time Window: ${labels.join(", ")}`);
    }
    if (values.preferredDate) {
      notes.push(`Preferred Date: ${dayjs(values.preferredDate).format("MMMM D, YYYY")}`);
    }
    if (values.preferredTimeWindow) {
      notes.push(`Preferred Time of Day: ${values.preferredTimeWindow}`);
    }
    notes.push("");
    
    // Inventory Items
    if (selectedInventory.length > 0) {
      notes.push("INVENTORY ITEMS:");
      selectedInventory.forEach((item, index) => {
        notes.push(`${index + 1}. ${item.inventoryRef || "No Ref"}`);
        if (item.clientInternalRef) {
          notes.push(`   Client Ref: ${item.clientInternalRef}`);
        }
        if (item.title) {
          notes.push(`   Title: ${item.title}`);
        }
        if (item.artistName) {
          notes.push(`   Artist: ${item.artistName}`);
        }
      });
      notes.push("");
    }
    
    // Special Instructions
    if (values.specialInstructions) {
      notes.push("SPECIAL INSTRUCTIONS:");
      notes.push(values.specialInstructions);
      notes.push("");
    }
    
    // Documents
    if (fileList.length > 0) {
      notes.push("ATTACHED DOCUMENTS:");
      fileList.forEach((file, index) => {
        notes.push(`${index + 1}. ${file.name} (${file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : "N/A"})`);
      });
      notes.push("");
    }
    
    notes.push("=== END OF REQUEST ===");
    
    return notes.join("\n");
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      if (!client?.id) {
        message.error("Client information not found. Please try again.");
        return;
      }

      if (selectedInventoryIds.length === 0) {
        message.error("Please select at least one inventory item.");
        return;
      }

      setLoading(true);

      // Get selected inventory details for notes
      const selectedInventory = selectedInventories;

      // Format notes with all request details
      const notes = formatRequestNotes(values, selectedInventory);

      // Map movement type to ServiceType
      const serviceType = mapMovementTypeToServiceType(values.movementType);

      // Determine location data based on movement type
      let locationId: string | undefined;
      let tempLocationData: { address?: string; city?: string; state?: string; zip?: string; name?: string } | undefined;

      if (values.movementType === "delivery_from_storage" && values.deliveryAddress) {
        // Try to find matching shipping address
        const matchingAddress = addressesToUse.find(
          (addr) => `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}` === values.deliveryAddress
        );
        if (matchingAddress) {
          locationId = matchingAddress.id;
        } else {
          // New address - use tempLocationData
          const addressParts = values.deliveryAddress.split(",").map((s: string) => s.trim());
          tempLocationData = {
            address: addressParts[0] || values.deliveryAddress,
            city: addressParts[1] || "",
            state: addressParts[2]?.split(" ")[0] || "",
            zip: addressParts[2]?.split(" ")[1] || "",
          };
        }
      } else if (values.movementType === "collect_for_storage" && values.pickupAddress) {
        const addressParts = values.pickupAddress.split(",").map((s: string) => s.trim());
        tempLocationData = {
          address: addressParts[0] || values.pickupAddress,
          city: addressParts[1] || "",
          state: addressParts[2]?.split(" ")[0] || "",
          zip: addressParts[2]?.split(" ")[1] || "",
        };
      } else if (values.movementType === "transport") {
        // For transport, we'll use pickup address as location
        if (values.pickupAddress) {
          const matchingAddress = addressesToUse.find(
            (addr) => `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}` === values.pickupAddress
          );
          if (matchingAddress) {
            locationId = matchingAddress.id;
          } else {
            const addressParts = values.pickupAddress.split(",").map((s: string) => s.trim());
            tempLocationData = {
              address: addressParts[0] || values.pickupAddress,
              city: addressParts[1] || "",
              state: addressParts[2]?.split(" ")[0] || "",
              zip: addressParts[2]?.split(" ")[1] || "",
            };
          }
        }
      } else if (values.movementType === "on_site_service" && values.serviceAddress) {
        const matchingAddress = addressesToUse.find(
          (addr) => `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}` === values.serviceAddress
        );
        if (matchingAddress) {
          locationId = matchingAddress.id;
        } else {
          const addressParts = values.serviceAddress.split(",").map((s: string) => s.trim());
          tempLocationData = {
            address: addressParts[0] || values.serviceAddress,
            city: addressParts[1] || "",
            state: addressParts[2]?.split(" ")[0] || "",
            zip: addressParts[2]?.split(" ")[1] || "",
          };
        }
      }

      // Step 1: Create service request (creates job ticket and service ticket with ESTIMATED status)
      const serviceClientId = values.subClientId || client.id;
      
      const serviceRequestPayload: any = {
        clientId: serviceClientId,
        subClientId: values.subClientId || undefined,
        serviceType: serviceType,
        preferredDate: values.preferredDate ? values.preferredDate.toISOString() : undefined,
        preferredTimeWindow: preferredTimeWindowCheckbox.length > 0 
          ? preferredTimeWindowCheckbox.join(", ") 
          : values.preferredTimeWindow,
        pickupAddress: values.pickupAddress,
        deliveryAddress: values.deliveryAddress,
        serviceAddress: values.serviceAddress,
        specialInstructions: values.specialInstructions,
        contactName: values.contactName || `${client?.firstName} ${client?.lastName}`,
        contactPhone: values.contactPhone || client?.contactPhone,
        inventoryIds: selectedInventoryIds,
        shippingAddressId: locationId,
        tempLocationData: tempLocationData,
        notes: notes, // All request details formatted as notes
      };

      const jobTicket = await createServiceRequestMutation.mutateAsync(serviceRequestPayload);

      // Step 2: Upload documents if any - link to the service ticket
      // The job ticket response includes serviceTicket array
      const serviceTicketId = jobTicket?.serviceTicket?.[0]?.id || jobTicket?.serviceTickets?.[0]?.id || jobTicket?._serviceTicketId;
      if (fileList.length > 0 && serviceTicketId) {
        const uploadPromises = fileList.map(async (file) => {
          if (file.originFileObj) {
            return fileServices.createFile({
              title: file.name || "Service Request Document",
              type: DocumentType.SUPPLEMENTARY,
              file: file.originFileObj,
              serviceTicketId: serviceTicketId,
              visibleToStaff: true,
            });
          }
        });

        await Promise.all(uploadPromises);
      }

      const serviceTicket = jobTicket?.serviceTicket?.[0] || jobTicket?.serviceTickets?.[0];
      const ticketSerial = serviceTicket?.serial || '';
      
      // Send notification to all admins
      try {
        // Get all admin users
        const adminUsers = await userService.getUsers({
          roles: [Role.ADMIN, Role.SUPER_ADMIN],
          page: 1,
          pageSize: 1000, // Get all admins
        });

        if (adminUsers.items.length > 0) {
          const adminUserIds = adminUsers.items.map((user) => user.id);
          
          // Format notification message
          const notificationSubject = `New Service Request from ${client?.companyName || `${client?.firstName} ${client?.lastName}`.trim() || 'Client'}`;
          const notificationBody = `A new service request has been submitted:\n\n${notes}\n\nService Ticket: ${ticketSerial || 'Pending'}`;

          // Send notification to all admins
          await notificationsAPI.sendManualMessage({
            subject: notificationSubject,
            body: notificationBody,
            recipientIds: adminUserIds,
            sendMode: 'individual',
          });
        }
      } catch (notificationError) {
        console.error('Error sending notification to admins:', notificationError);
        // Don't fail the request if notification fails
      }
      
      message.success(
        `Service request submitted successfully! Service ticket ${ticketSerial ? `(${ticketSerial})` : ''} has been created with status ESTIMATED. Our team will contact you to confirm the details.`
      );
      
      form.resetFields();
      setSelectedInventoryIds([]);
      setFileList([]);
      setSelectedSubClientId(undefined);
      setPreferredTimeWindowCheckbox([]);
      onClose();
    } catch (error: any) {
      console.error("Error submitting service request:", error);
      // Extract error message from various possible error structures
      let errorMessage = "Failed to submit service request. Please try again.";
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      // Show error notification with longer duration for visibility
      message.error({
        content: errorMessage,
        duration: 5,
      });
    } finally {
      setLoading(false);
    }
  };

  const isLoading = loading || createServiceRequestMutation.isPending;

  // Parse address string into components
  const parseAddress = (addressString: string): { address: string; city: string; state: string; zip: string } => {
    // Try to parse common address formats
    // Format: "123 Main St, City, State ZIP" or "123 Main St, City, State 12345"
    const parts = addressString.split(",").map((p) => p.trim());
    
    const address = parts[0] || addressString;
    let city = "";
    let state = "";
    let zip = "";

    if (parts.length >= 2) {
      city = parts[1] || "";
    }

    if (parts.length >= 3) {
      // Last part might be "State ZIP" or just "ZIP"
      const lastPart = parts[2] || "";
      const stateZipMatch = lastPart.match(/^([A-Z]{2})\s+(\d{5}(?:-\d{4})?)$/i);
      
      if (stateZipMatch) {
        state = stateZipMatch[1].toUpperCase();
        zip = stateZipMatch[2];
      } else {
        // Try to split by space
        const stateZipParts = lastPart.split(/\s+/);
        if (stateZipParts.length >= 2) {
          state = stateZipParts[0].toUpperCase();
          zip = stateZipParts.slice(1).join(" ");
        } else if (stateZipParts.length === 1) {
          // Could be just state or just zip
          if (/^\d{5}(?:-\d{4})?$/.test(stateZipParts[0])) {
            zip = stateZipParts[0];
          } else {
            state = stateZipParts[0].toUpperCase();
          }
        }
      }
    }

    return { address, city, state, zip };
  };

  // Save new address to client (subclient if selected, otherwise main client)
  const saveNewAddress = async (addressString: string, addressType: 'pickup' | 'delivery' | 'service'): Promise<void> => {
    const targetClientId = selectedSubClientId || client?.id;
    if (!targetClientId) {
      message.error("Client information not found");
      return;
    }

    const parsed = parseAddress(addressString);

    // Get current client data - use subclient if selected, otherwise main client
    const currentClient = selectedSubClientId ? selectedSubClientData : client;
    const existingAddresses = currentClient?.shippingAddresses || [];
    
    const newAddress = {
      address: parsed.address,
      city: parsed.city || "Unknown",
      state: parsed.state || "Unknown",
      zip: parsed.zip || "",
      addressName: `${addressType.charAt(0).toUpperCase() + addressType.slice(1)} Address`,
    };

    const updatedAddresses = [...existingAddresses, newAddress];

    // Update client with new address
    const formData = new FormData();
    formData.append("shippingAddresses", JSON.stringify(updatedAddresses));

    await updateClientMutation.mutateAsync({
      id: targetClientId,
      formData,
    });

    // Refresh the subclient data if we saved to a subclient
    if (selectedSubClientId) {
      queryClient.invalidateQueries({
        queryKey: ["clientManagement/client", selectedSubClientId],
      });
    } else {
      // Refresh user data for main client
      const updatedUser = await userService.getUsers({ 
        roles: [Role.CLIENT],
        search: client?.id,
        pageSize: 1 
      });
      if (updatedUser.items.length > 0) {
        const { useAuth } = await import("~/store");
        useAuth.getState().setMe(updatedUser.items[0]);
      }
    }

    message.success("Address saved successfully!");
  };

  // Initialize movement type and pre-selected inventory when sidebar opens
  useEffect(() => {
    if (open) {
      const initialType: MovementType = "delivery_from_storage";
      setMovementType(initialType);
      form.setFieldsValue({ movementType: initialType });
      
      // Set pre-selected inventory IDs if provided
      if (preSelectedInventoryIds.length > 0) {
        setSelectedInventoryIds(preSelectedInventoryIds);
      }
    } else {
      // Reset when closing
      setMovementType(undefined);
      form.resetFields();
      setSelectedInventoryIds([]);
      setFileList([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]); // Only depend on 'open' to avoid infinite loops

  // Handle preSelectedInventoryIds updates separately when sidebar is open
  useEffect(() => {
    if (open && preSelectedInventoryIds.length > 0) {
      // Only update if the IDs are actually different
      setSelectedInventoryIds((prev) => {
        const prevSorted = [...prev].sort().join(',');
        const newSorted = [...preSelectedInventoryIds].sort().join(',');
        if (prevSorted !== newSorted) {
          return preSelectedInventoryIds;
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, preSelectedInventoryIds.join(',')]); // Use join to create stable string comparison

  if (!open) return null;

  return (
    <div className="h-full flex relative max-w-full overflow-visible">
      {/* Main Sidebar */}
      <div className="h-full flex flex-col bg-[#fffff5] border-l-2 border-black flex-1 min-w-0 max-w-full overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b-2 border-black bg-[#fffff5]">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Package size={20} className="text-black flex-shrink-0" />
            <h2 className="text-lg font-bold text-black uppercase tracking-wide !mb-0">
              Request Service
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[#fffef5] transition-colors flex-shrink-0 ml-2"
            aria-label="Close sidebar"
          >
            <X size={20} className="text-black" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <Form 
            form={form} 
            layout="vertical" 
            initialValues={{ movementType: "delivery_from_storage" }}
            onValuesChange={(changedValues) => {
              if (changedValues.movementType !== undefined) {
                setMovementType(changedValues.movementType);
                // Clear address fields when movement type changes
                form.setFieldsValue({
                  pickupAddress: undefined,
                  deliveryAddress: undefined,
                  serviceAddress: undefined,
                });
              }
            }}
          >
            {/* Subclient Selection - Only show if client has subclients */}
            {subClientsData?.items && subClientsData.items.length > 0 && (
              <Form.Item
                name="subClientId"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-black">
                    Subclient (Optional)
                  </span>
                }
              >
                <Select
                  placeholder="Select subclient (leave blank for main client)"
                  className="border-2 border-black"
                  allowClear
                  onChange={(value) => {
                    setSelectedSubClientId(value);
                    // Clear selected inventory when subclient changes
                    setSelectedInventoryIds([]);
                  }}
                >
                  {subClientsData.items.map((subClient) => (
                    <Option key={subClient.id} value={subClient.id}>
                      {subClient.companyName || `${subClient.firstName} ${subClient.lastName}`.trim() || subClient.clientRef}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            {/* Movement Type */}
            <Form.Item
              name="movementType"
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black">
                  Movement Type
                </span>
              }
              rules={[{ required: true, message: "Please select a movement type" }]}
            >
              <Select
                placeholder="Select movement type"
                className="border-2 border-black"
                onChange={(value) => setMovementType(value)}
              >
                <Option value="delivery_from_storage">
                  <div className="flex items-center gap-2">
                    <span>Delivery from Storage</span>
                  </div>
                </Option>
                <Option value="collect_for_storage">
                  <div className="flex items-center gap-2">
                    <span>Collect for Storage/Hold</span>
                  </div>
                </Option>
                <Option value="transport">
                  <div className="flex items-center gap-2">
                    <span>Transport</span>
                  </div>
                </Option>
                <Option value="on_site_service">
                  <div className="flex items-center gap-2">
                    <span>On Site Service</span>
                  </div>
                </Option>
              </Select>
            </Form.Item>

            {/* Inventory Selection */}
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black flex items-center gap-2">
                  <Package size={14} />
                  Inventory Items
                </span>
              }
            >
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setIsInventorySidebarOpen(true)}
                  className="w-full border-2 border-black bg-[#fffff5] text-black px-4 py-3 font-bold text-sm uppercase tracking-wide hover:bg-[#fffef5] transition-all flex items-center justify-center gap-2"
                >
                  <Plus size={16} />
                  Select Inventory Items
                </button>
                {selectedInventories.length > 0 && (
                  <div className="border-2 border-black bg-[#fffff5] p-3 space-y-2">
                    <div className="text-xs font-bold uppercase tracking-wide text-black">
                      Selected ({selectedInventories.length})
                    </div>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {selectedInventories.map((item) => (
                        <div
                          key={item.id}
                          className="p-2 border-2 border-black bg-[#fffef5] space-y-1"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              {/* Client Ref Number */}
                              <div className="text-sm font-bold uppercase tracking-wide text-black truncate">
                                {item.inventoryRef || "No Ref"}
                              </div>
                              {/* Client Internal Ref */}
                              {item.clientInternalRef && (
                                <div className="text-xs font-medium text-black truncate mt-0.5">
                                  Client Ref: {item.clientInternalRef}
                                </div>
                              )}
                              {/* Title */}
                              {item.title && (
                                <div className="text-xs font-medium text-black truncate mt-0.5">
                                  {item.title}
                                </div>
                              )}
                              {/* Status */}
                              <div className="mt-1.5">
                                {getStatusBadge(item.status)}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedInventoryIds((prev) =>
                                  prev.filter((id) => id !== item.id)
                                )
                              }
                              className="ml-2 p-1 hover:bg-black hover:text-white transition-colors flex-shrink-0"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </Form.Item>

            {/* Pickup Address - Show for Collect for Storage and Transport */}
            {(movementType === "collect_for_storage" || movementType === "transport") && (
              <Form.Item
                name="pickupAddress"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-black flex items-center gap-2">
                    <MapPin size={14} />
                    Pickup Address
                  </span>
                }
                rules={[{ required: true, message: "Please enter or select a pickup address" }]}
              >
                {movementType === "collect_for_storage" ? (
                  <Input
                    placeholder="Enter pickup address..."
                    className="border-2 border-black"
                  />
                ) : (
                  <Select
                    placeholder="Select from saved addresses or enter new"
                    className="border-2 border-black"
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <div className="p-2 border-t-2 border-black">
                          <Input
                            placeholder="Or type a new address..."
                            className="border-2 border-black"
                            onPressEnter={async (e) => {
                              const value = (e.target as HTMLInputElement).value.trim();
                              if (value) {
                                // Check if it's already a saved address
                                const isExistingAddress = addressesToUse.some(
                                  (addr) => `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}` === value
                                );
                                
                                if (!isExistingAddress) {
                                  // Save the new address
                                  try {
                                    await saveNewAddress(value, 'pickup');
                                    // Set the form value after saving
                                    form.setFieldValue("pickupAddress", value);
                                  } catch (error) {
                                    console.error("Error saving address:", error);
                                    // Still set the value even if save fails
                                    form.setFieldValue("pickupAddress", value);
                                  }
                                } else {
                                  form.setFieldValue("pickupAddress", value);
                                }
                                // Clear the input
                                (e.target as HTMLInputElement).value = "";
                              }
                            }}
                          />
                        </div>
                      </>
                    )}
                  >
                    {addressesToUse.map((addr, index) => {
                      const fullAddress = `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}`;
                      return (
                        <Option
                          key={index}
                          value={fullAddress}
                        >
                          <div className="min-w-0">
                            <div className="font-bold truncate">{addr.addressName || `Address ${index + 1}`}</div>
                            <div className="text-xs text-black break-words">
                              {fullAddress}
                            </div>
                          </div>
                        </Option>
                      );
                    })}
                  </Select>
                )}
              </Form.Item>
            )}

            {/* Delivery Address - Show for Delivery from Storage and Transport */}
            {(movementType === "delivery_from_storage" || movementType === "transport") && (
              <Form.Item
                name="deliveryAddress"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-black flex items-center gap-2">
                    <MapPin size={14} />
                    Delivery Address
                  </span>
                }
                rules={[{ required: true, message: "Please select or enter a delivery address" }]}
              >
                <Select
                  placeholder="Select from saved addresses or enter new"
                  className="border-2 border-black [&_.ant-select-selector]:!min-h-[42px] [&_.ant-select-selection-item]:!truncate [&_.ant-select-selection-item]:!max-w-full"
                  maxTagCount="responsive"
                  showSearch
                  filterOption={false}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <div className="p-2 border-t-2 border-black">
                        <Input
                          placeholder="Or type a new address..."
                          className="border-2 border-black"
                          onPressEnter={async (e) => {
                            const value = (e.target as HTMLInputElement).value.trim();
                            if (value) {
                              // Check if it's already a saved address
                              const isExistingAddress = addressesToUse.some(
                                (addr) => `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}` === value
                              );
                              
                              if (!isExistingAddress) {
                                // Save the new address
                                try {
                                    await saveNewAddress(value, 'delivery');
                                    // Set the form value after saving
                                    form.setFieldValue("deliveryAddress", value);
                                } catch (error) {
                                  console.error("Error saving address:", error);
                                  // Still set the value even if save fails
                                  form.setFieldValue("deliveryAddress", value);
                                }
                              } else {
                                form.setFieldValue("deliveryAddress", value);
                              }
                              // Clear the input
                              (e.target as HTMLInputElement).value = "";
                            }
                          }}
                        />
                      </div>
                    </>
                  )}
                >
                    {addressesToUse.map((addr, index) => {
                    const fullAddress = `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}`;
                    return (
                      <Option
                        key={index}
                        value={fullAddress}
                      >
                        <div className="min-w-0">
                          <div className="font-bold truncate">{addr.addressName || `Address ${index + 1}`}</div>
                          <div className="text-xs text-black break-words">
                            {fullAddress}
                          </div>
                        </div>
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            )}

            {/* Service Address - Show for On Site Service */}
            {movementType === "on_site_service" && (
              <Form.Item
                name="serviceAddress"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-black flex items-center gap-2">
                    <MapPin size={14} />
                    Service Address
                  </span>
                }
                rules={[{ required: true, message: "Please select or enter a service address" }]}
              >
                <Select
                  placeholder="Select from saved addresses or enter new"
                  className="border-2 border-black [&_.ant-select-selector]:!min-h-[42px] [&_.ant-select-selection-item]:!truncate [&_.ant-select-selection-item]:!max-w-full"
                  maxTagCount="responsive"
                  showSearch
                  filterOption={false}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <div className="p-2 border-t-2 border-black">
                        <Input
                          placeholder="Or type a new address..."
                          className="border-2 border-black"
                          onPressEnter={async (e) => {
                            const value = (e.target as HTMLInputElement).value.trim();
                            if (value) {
                              // Check if it's already a saved address
                              const isExistingAddress = addressesToUse.some(
                                (addr) => `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}` === value
                              );
                              
                              if (!isExistingAddress) {
                                // Save the new address
                                try {
                                    await saveNewAddress(value, 'service');
                                    // Set the form value after saving
                                    form.setFieldValue("serviceAddress", value);
                                } catch (error) {
                                  console.error("Error saving address:", error);
                                  // Still set the value even if save fails
                                  form.setFieldValue("serviceAddress", value);
                                }
                              } else {
                                form.setFieldValue("serviceAddress", value);
                              }
                              // Clear the input
                              (e.target as HTMLInputElement).value = "";
                            }
                          }}
                        />
                      </div>
                    </>
                  )}
                >
                    {addressesToUse.map((addr, index) => {
                    const fullAddress = `${addr.address}, ${addr.city}, ${addr.state} ${addr.zip}`;
                    return (
                      <Option
                        key={index}
                        value={fullAddress}
                      >
                        <div className="min-w-0">
                          <div className="font-bold truncate">{addr.addressName || `Address ${index + 1}`}</div>
                          <div className="text-xs text-black break-words">
                            {fullAddress}
                          </div>
                        </div>
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            )}

            {/* Preferred Time Window - Checkboxes FIRST */}
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black">
                  Preferred Time Window
                </span>
              }
            >
              <div className="space-y-3">
                {/* Predefined Options - Checkboxes */}
                <Checkbox.Group
                  className="grid grid-cols-2 gap-2 w-full"
                  value={preferredTimeWindowCheckbox}
                  onChange={(checkedValues) => {
                    setPreferredTimeWindowCheckbox(checkedValues as string[]);
                  }}
                >
                  <Checkbox
                    value="ASAP"
                    className="neo-brutalist-checkbox"
                  >
                    ASAP
                  </Checkbox>
                  <Checkbox
                    value="within_1_week"
                    className="neo-brutalist-checkbox"
                  >
                    Within 1 Week
                  </Checkbox>
                  <Checkbox
                    value="2_weeks"
                    className="neo-brutalist-checkbox"
                  >
                    2 Weeks
                  </Checkbox>
                  <Checkbox
                    value="beyond_2_weeks"
                    className="neo-brutalist-checkbox"
                  >
                    Beyond 2 Weeks
                  </Checkbox>
                </Checkbox.Group>
              </div>
            </Form.Item>

            {/* Preferred Date - Not mandatory */}
            <Form.Item
              name="preferredDate"
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black flex items-center gap-2">
                  <Calendar size={14} />
                  Preferred Date (Optional)
                </span>
              }
            >
              <DatePicker
                className="w-full border-2 border-black"
                disabledDate={(current) => current && current < dayjs().startOf("day")}
                format="MMMM D, YYYY"
              />
            </Form.Item>

            {/* Preferred Time of Day - Not mandatory */}
            <Form.Item
              name="preferredTimeWindow"
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black">
                  Preferred Time of Day (Optional)
                </span>
              }
            >
              <Select
                placeholder="Select preferred time (e.g., Morning)"
                className="border-2 border-black [&_.ant-select-selector]:!min-h-[42px] [&_.ant-select-selection-item]:!truncate [&_.ant-select-selection-item]:!max-w-full"
                value={form.getFieldValue("preferredTimeWindow")}
                onChange={(value) => {
                  form.setFieldsValue({ preferredTimeWindow: value });
                }}
                allowClear
              >
                <Option value="morning">Morning (8am - 12pm)</Option>
                <Option value="afternoon">Afternoon (12pm - 5pm)</Option>
                <Option value="flexible">Flexible</Option>
              </Select>
            </Form.Item>

            {/* Contact Information */}
            <div className="grid grid-cols-1 gap-4">
              <Form.Item
                name="contactName"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-black">
                    Contact Name
                  </span>
                }
                initialValue={client ? `${client.firstName} ${client.lastName}` : ""}
              >
                <Input
                  placeholder="Contact name for this delivery"
                  className="border-2 border-black"
                />
              </Form.Item>

              <Form.Item
                name="contactPhone"
                label={
                  <span className="text-xs font-bold uppercase tracking-wide text-black">
                    Contact Phone
                  </span>
                }
                initialValue={client?.contactPhone || ""}
              >
                <Input
                  placeholder="Phone number"
                  className="border-2 border-black"
                />
              </Form.Item>
            </div>

            {/* Special Instructions */}
            <Form.Item
              name="specialInstructions"
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black flex items-center gap-2">
                  <FileText size={14} />
                  Special Instructions
                </span>
              }
            >
              <TextArea
                rows={4}
                placeholder="Any special handling requirements, access instructions, or notes..."
                className="border-2 border-black"
              />
            </Form.Item>

            {/* Document Upload */}
            <Form.Item
              label={
                <span className="text-xs font-bold uppercase tracking-wide text-black flex items-center gap-2">
                  <UploadIcon size={14} />
                  Documents (Optional)
                </span>
              }
            >
              <div className="space-y-2">
                <Upload
                  multiple
                  beforeUpload={(file) => {
                    // Validate file type (PDF only)
                    const isValidType = file.type === "application/pdf";
                    if (!isValidType) {
                      message.error("Only PDF files are allowed");
                      return Upload.LIST_IGNORE;
                    }

                    // Validate file size (10MB max)
                    const isLt10M = file.size / 1024 / 1024 < 10;
                    if (!isLt10M) {
                      message.error("File must be smaller than 10MB");
                      return Upload.LIST_IGNORE;
                    }

                    return false; // Prevent auto upload
                  }}
                  fileList={fileList}
                  onChange={({ fileList: newFileList }) => {
                    setFileList(newFileList);
                  }}
                  onRemove={(file) => {
                    const index = fileList.indexOf(file);
                    const newFileList = fileList.slice();
                    newFileList.splice(index, 1);
                    setFileList(newFileList);
                  }}
                  accept=".pdf"
                  className="[&_.ant-upload]:!border-2 [&_.ant-upload]:!border-black [&_.ant-upload]:!border-dashed [&_.ant-upload]:!bg-[#fffff5] [&_.ant-upload]:!rounded-none [&_.ant-upload-select]:!w-full"
                >
                  <button
                    type="button"
                    className="w-full border-2 border-black bg-[#fffff5] text-black px-4 py-3 font-bold text-sm uppercase tracking-wide hover:bg-[#fffef5] transition-all flex items-center justify-center gap-2"
                  >
                    <UploadIcon size={16} />
                    Upload PDF Documents
                  </button>
                </Upload>
                {fileList.length > 0 && (
                  <div className="border-2 border-black bg-[#fffff5] p-3 space-y-2">
                    <div className="text-xs font-bold uppercase tracking-wide text-black">
                      Uploaded ({fileList.length})
                    </div>
                    <div className="space-y-1">
                      {fileList.map((file, index) => (
                        <div
                          key={file.uid || index}
                          className="flex items-center justify-between p-2 border-2 border-black bg-[#fffef5]"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-bold text-black truncate">
                              {file.name}
                            </div>
                            {file.size && (
                              <div className="text-xs text-black">
                                {(file.size / 1024 / 1024).toFixed(2)} MB
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newFileList = fileList.filter((f) => f.uid !== file.uid);
                              setFileList(newFileList);
                            }}
                            className="ml-2 p-1 hover:bg-black hover:text-white transition-colors flex-shrink-0"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="text-xs text-black font-medium">
                  PDF files only, max 10MB per file
                </div>
              </div>
            </Form.Item>
          </Form>

          <div className="mt-4 p-3 border-2 border-black bg-[#fffff5]">
            <div className="text-xs font-bold uppercase tracking-wide text-black mb-1">
              Note
            </div>
            <div className="text-sm text-black">
              After submitting your request, our team will review the details
              and contact you to confirm scheduling and provide a quote if applicable.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t-2 border-black flex gap-3 bg-[#fffff5]">
          <button
            onClick={onClose}
            className="flex-1 border-2 border-black bg-[#fffff5] text-black px-4 py-3 font-bold text-sm uppercase tracking-wide hover:bg-[#fffef5] transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="flex-1 border-2 border-black bg-black text-white px-4 py-3 font-bold text-sm uppercase tracking-wide hover:opacity-90 transition-all disabled:opacity-50"
          >
            {isLoading ? "Submitting..." : "Submit Request"}
          </button>
        </div>
      </div>

      {/* Inventory Selection Sidebar (nested) */}
      {isInventorySidebarOpen && (
        <>
          <div
            className={`absolute right-full top-0 h-full bg-[#fffff5] transition-all duration-300 ease-in-out border-r-2 border-black max-w-[100vw] overflow-x-hidden ${
              isInventorySidebarOpen ? "w-[400px] translate-x-0" : "w-[400px] translate-x-full"
            }`}
            style={{ zIndex: 60 }}
          >
            <InventorySelectionSidebar
              selectedInventoryIds={selectedInventoryIds}
              onSelect={(ids) => setSelectedInventoryIds(ids)}
              onClose={() => setIsInventorySidebarOpen(false)}
              subClientId={selectedSubClientId}
            />
          </div>
          {/* Overlay for nested sidebar on mobile */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-[55] lg:hidden pointer-events-auto"
            onClick={() => setIsInventorySidebarOpen(false)}
          />
        </>
      )}
    </div>
  );
};
